import { useState } from "react";
import { X } from "lucide-react";
import notify from "@/services/notify.tsx";
import { useForm } from "react-hook-form";
import CadastroColaboradorService from "@/services/cadastroColaborador";
import { useTranslation } from "react-i18next";

interface AddNewUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUserCreated: (userId: number) => void;
  initialEmail?: string;
}

interface FormInputs {
  email: string;
}

export function AddNewUserModal({
  isOpen,
  onClose,
  onUserCreated,
  initialEmail = "",
}: AddNewUserModalProps) {
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<FormInputs>({
    defaultValues: {
      email: initialEmail,
    },
  });

  const onSubmit = async (data: FormInputs) => {
    setIsSubmitting(true);
    const toastId = notify.loading("Criando usuário...");
    try {
      const formData = new FormData();
      formData.append("email", data.email);
      formData.append("is_new", "true");
      formData.append("is_ad_link", "true");

      const response =
        await CadastroColaboradorService.cadastrarColaborador(formData);

      if (response.status === 200) {
        notify.update(
          toastId,
          "success",
          t("resumoDesk.adLink.user_created_success"),
        );
        onUserCreated(response.data.user.id);
        reset();
        onClose();
      } else {
        notify.update(
          toastId,
          "error",
          t("resumoDesk.adLink.errors.create_user"),
        );
      }
    } catch (error: any) {
      console.error("Erro ao criar usuário:", error);
      notify.update(
        toastId,
        "error",
        error?.response?.data?.error ||
          t("resumoDesk.adLink.errors.create_user_try_again"),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      reset();
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-2xl w-full max-w-md flex flex-col animate-fadeIn">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-semibold text-gray-800">
            {t("resumoDesk.adLink.register_new_user")}
          </h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            disabled={isSubmitting}
          >
            <X size={24} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-4">
          <div className="flex flex-col gap-2">
            <label
              htmlFor="email"
              className="text-sm font-medium text-gray-700"
            >
              {t("resumoDesk.adLink.email")}{" "}
              <span className="text-red-500">*</span>
            </label>
            <input
              id="email"
              type="email"
              placeholder="usuario@email.com"
              disabled={isSubmitting}
              className="px-4 py-2.5 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-azul-400 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
              {...register("email", {
                required: t("resumoDesk.adLink.email_required"),
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: t("resumoDesk.adLink.invalid_email"),
                },
              })}
            />
            {errors.email && (
              <span className="text-xs text-red-500">
                {errors.email.message}
              </span>
            )}
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-4">
            <p className="text-xs text-gray-700">
              <strong>{t("resumoDesk.adLink.attention")}:</strong>{" "}
              {t("resumoDesk.adLink.registration_warning")}
            </p>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={handleClose}
              disabled={isSubmitting}
              className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
            >
              {t("common:cancel")}
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 px-4 py-2.5 bg-azul-400 hover:bg-azul-500 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
            >
              {isSubmitting
                ? t("resumoDesk.adLink.creating")
                : t("resumoDesk.adLink.create_user")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
